import _clonedeep from 'lodash.clonedeep';
import {
  packingMaterialTypes,
  getDimensionsString,
  orderStatuses,
  unitOfMeasure,
  formatContactApi,
  makeuuid,
  hasPhysicalItemTrackingValues,
} from '../../lib';
import cloneDeep from 'lodash.clonedeep';
import { captureMessage } from '@sentry/react';

export function generateOrderPayload({
  packages,
  allPhysicalItems,
  orderData,
  status = 'None',
}) {
  const {
    firstValidation,
    lineItems,
    notes,
    shipToContact,
    items,
    ...otherOrderData
  } = orderData;
  const payload = { ...otherOrderData, status };
  let remainingDbPhysicalItems = allPhysicalItems;

  const packagesArr = Object.keys(packages).map((key) => {
    const currentPackage = packages[key];
    const {
      items,
      metadata = {},
      material,
      ...otherPackageValues
    } = currentPackage;
    const isPickAndStick = !!metadata.isPickAndStick;
    const isCustomMode = !!metadata.isCustomMode;
    let payload = {
      ...otherPackageValues,
      mode: isCustomMode
        ? 'Unknown'
        : isPickAndStick
          ? 'PickAndStick'
          : 'PickAndPack',
      isSealed: metadata.isCompleted,
      weightPounds: metadata.weightPounds,
      items:
        items &&
        Object.keys(items).map((k) => {
          const item = items[k];
          if (!allPhysicalItems) {
            return item;
          }
          const { physicalItems, ...rest } = item;
          const { itemPhysicalItems, remainingDbItems } =
            generateItemPhysicalItems({
              sku: k,
              itemPhysicalItems: physicalItems,
              dbPhysicalItems: remainingDbPhysicalItems,
              itemQty: item.quantity,
              orderCreationType: orderData.orderCreationType,
              orderId: orderData.id,
            });
          if (remainingDbItems) {
            remainingDbPhysicalItems = remainingDbItems;
          }
          return {
            ...rest,
            physicalItems: itemPhysicalItems,
          };
        }),
      materials:
        material &&
        Object.keys(material).map((k) => {
          const {
            sku,
            dimensions,
            description,
            packingMaterialTypeDisplay,
            ...otherMaterial
          } = material[k];
          return {
            ...otherMaterial,
            packingMaterialID: sku,
          };
        }),
    };
    // package dimensions
    if (isPickAndStick || isCustomMode) {
      payload = { ...payload, ...getPickAndStickDimensions(currentPackage) };
    }
    return payload;
  });

  payload.packages = packagesArr;
  return payload;
}

function generateItemPhysicalItems({
  sku,
  itemPhysicalItems = {},
  dbPhysicalItems = {},
  itemQty,
  orderCreationType,
  orderId,
}) {
  const itemPhysicalItemsKeys = Object.keys(itemPhysicalItems ?? {}).filter(
    (key) =>
      orderCreationType === 'Extensiv' ||
      hasPhysicalItemTrackingValues(itemPhysicalItems[key]) ||
      // Preserve explicit references so the server can reject invalid/mismatched IDs.
      itemPhysicalItems[key].id != null,
  );
  // If we did inventory tracking then the physical items should already be there
  if (itemPhysicalItemsKeys.length) {
    const items = itemPhysicalItemsKeys.map((p) => itemPhysicalItems[p]);
    const isBlankReference = (pi) =>
      orderCreationType !== 'Extensiv' && !hasPhysicalItemTrackingValues(pi);
    const totalQty = items.reduce(
      (sum, pi) => sum + (isBlankReference(pi) ? 0 : pi.quantity || 1),
      0,
    );

    // If the total quantity across all physical items exceeds the package
    // item quantity, the data is corrupt (order-level total was persisted
    // on each package item). Normalize so the total matches itemQty.
    if (totalQty > itemQty) {
      captureMessage(
        'Package physical-item quantity exceeds package-item quantity',
        {
          level: 'error',
          tags: {
            ticket: 'C3PL-3732',
            operation: 'generate-process-payload',
            orderId: orderId?.toString() || 'unknown',
            sku,
            orderCreationType: orderCreationType || 'unknown',
          },
          extra: { itemQty, physicalItemQty: totalQty },
        },
      );
      const normalized = [];
      let remaining = itemQty;
      for (const item of items) {
        if (isBlankReference(item)) {
          normalized.push(item);
          continue;
        }
        if (remaining <= 0) continue;
        const qty = Math.min(item.quantity || 1, remaining);
        normalized.push({ ...item, quantity: qty });
        remaining -= qty;
      }
      return {
        itemPhysicalItems: normalized,
        remainingDbItems: removeAssignedPhysicalItemsFromPool({
          sku,
          dbPhysicalItems,
          assignedPhysicalItems: normalized,
          orderCreationType,
        }),
      };
    }

    return {
      itemPhysicalItems: items,
      remainingDbItems: removeAssignedPhysicalItemsFromPool({
        sku,
        dbPhysicalItems,
        assignedPhysicalItems: items,
        orderCreationType,
      }),
    };
  }

  const dbItemPhysicalItems = dbPhysicalItems[sku];
  if (orderCreationType !== 'Extensiv') {
    if (
      Array.isArray(dbItemPhysicalItems) &&
      dbItemPhysicalItems.some(hasPhysicalItemTrackingValues)
    ) {
      captureMessage(
        'Tracked package item has no packed physical items; omitting physical items from payload',
        {
          level: 'error',
          tags: {
            ticket: 'C3PL-3732',
            operation: 'generate-process-payload',
            orderId: orderId?.toString() || 'unknown',
            sku,
            orderCreationType: orderCreationType || 'unknown',
          },
          extra: {
            itemQty,
            availableDbPhysicalItemRows: dbItemPhysicalItems.length,
          },
        },
      );
    }
    return { itemPhysicalItems: [] };
  }

  // If no db physical items and no item physical items
  if (!Array.isArray(dbItemPhysicalItems)) return { itemPhysicalItems: [] };

  // Extensiv's legacy flow assigns one DB physical-item row per unit and pads
  // uncovered quantities with empty placeholders. Keep this isolated from the
  // quantity-aware inventory pool used by every other creation type.
  const remainingDbItemPhysicalItems = _clonedeep(dbItemPhysicalItems);
  const selectedDbItems = remainingDbItemPhysicalItems.splice(0, itemQty);
  const returnPhysicalItems = [];
  for (let i = 0; i < itemQty; i++) {
    returnPhysicalItems.push(
      selectedDbItems[i] || {
        lotNumber: null,
        serialNumber: null,
        expirationDate: null,
      },
    );
  }

  const newState = {
    ...dbPhysicalItems,
    [sku]: remainingDbItemPhysicalItems,
  };
  return { itemPhysicalItems: returnPhysicalItems, remainingDbItems: newState };
}

function removeAssignedPhysicalItemsFromPool({
  sku,
  dbPhysicalItems,
  assignedPhysicalItems,
  orderCreationType,
}) {
  if (orderCreationType !== 'Extensiv') return undefined;

  const dbItemsForSku = dbPhysicalItems[sku];
  if (!Array.isArray(dbItemsForSku)) return undefined;

  const assignedIds = new Set(
    assignedPhysicalItems
      .map((physicalItem) => physicalItem.id)
      .filter((id) => id !== undefined && id !== null)
      .map(String),
  );
  if (!assignedIds.size) return undefined;

  return {
    ...dbPhysicalItems,
    [sku]: dbItemsForSku.filter(
      (physicalItem) => !assignedIds.has(String(physicalItem.id)),
    ),
  };
}

function getPickAndStickDimensions(currentPackage) {
  return {
    pickAndStickLengthInches: currentPackage.pickAndStickLengthInches,
    pickAndStickWidthInches: currentPackage.pickAndStickWidthInches,
    pickAndStickHeightInches: currentPackage.pickAndStickHeightInches,
    weightPounds: currentPackage.weightPounds,
  };
}

/**
 * Updates the items remaining, items completed, and packages state
 * @param {object} data The incoming payload
 * @param {object} itemsObj The order items as object with the sku as the key
 * @param {function} dispatch
 */
export function syncIncomingCompletedOrderFields(data, itemsObj) {
  const {
    packages: incomingPackages,
    status,
    physicalItems: allPhysicalItems,
  } = data;
  const assignedPhysicalItemIds = new Set();

  const packages = incomingPackages.reduce((acc, cur, index) => {
    const {
      mode,
      weightPounds,
      materials,
      items: incomingItems,
      childPackages,
      isSealed,
      ...rest
    } = cur;

    const material = generateIncomingMaterial(materials);

    const items = generateIncomingItems({ items: incomingItems, itemsObj });

    Object.keys(items).forEach((i) => {
      Object.keys(items[i]?.physicalItems ?? {}).forEach((pid) =>
        assignedPhysicalItemIds.add(pid),
      );
    });

    acc[index + 1] = {
      ...rest,
      weightPounds,
      metadata: {
        isCompleted: true,
        isPickAndStick: mode === 'PickAndStick',
        weightPounds,
      },
      material,
      items,
    };
    return acc;
  }, {});

  const physicalItems = generateCompletedOrderPhysicalItemsState(
    allPhysicalItems,
    assignedPhysicalItemIds,
    incomingPackages,
    data.orderCreationType,
  );

  if (status === orderStatuses.HOLD || status === orderStatuses.PAUSED) {
    const { itemsCompleted, itemsRemaining } = syncItemsRemainingCompleted(
      incomingPackages,
      itemsObj,
    );
    return { packages, physicalItems, itemsCompleted, itemsRemaining };
  }
  return { packages, physicalItems, itemsCompleted: itemsObj };
}

function syncItemsRemainingCompleted(packages, itemsObj) {
  const groupedUomData = {};

  const allItemsInPackages = packages.reduce((acc, cur) => {
    const { items: _items, childPackages } = cur;
    const items = Array.isArray(_items) ? cloneDeep(_items) : [];

    if (Array.isArray(childPackages)) {
      childPackages.forEach((cp) => {
        const { items: _childItems } = cp;
        const childItems = _childItems || [];
        items.push(...childItems);
      });
    }

    acc.push(...items);
    items?.forEach((i) => {
      const { customerItemID, packedPerUnitOfMeasureCount } = i;
      if (packedPerUnitOfMeasureCount) {
        groupedUomData[customerItemID] = groupedUomData[customerItemID] || {
          [unitOfMeasure.MASTER]: 0,
          [unitOfMeasure.SECONDARY]: 0,
          [unitOfMeasure.PRIMARY]: 0,
        };
        const counts = normalizePackedUnitCounts(packedPerUnitOfMeasureCount);
        for (const unit of Object.values(unitOfMeasure)) {
          groupedUomData[customerItemID][unit] += counts[unit] ?? 0;
        }
      }
    });
    return acc;
  }, []);

  let itemsRemaining = _clonedeep(itemsObj);
  const itemsCompleted = {};
  allItemsInPackages.forEach((i) => {
    const { sku, customerItemID, quantity } = i;
    const curItem = itemsRemaining[sku];
    if (!curItem) return;
    const itemRemainingQty = curItem.quantity - quantity;
    // items remaining
    if (itemRemainingQty < 1) {
      const { [sku]: itemToRemove, ...rest } = itemsRemaining;
      itemsRemaining = rest;
    } else {
      curItem.quantity = itemRemainingQty;
      curItem.packedPerUnitOfMeasureCount = groupedUomData[customerItemID + ''];
    }
    // items completed
    itemsCompleted[sku] = itemsCompleted[sku] || { ...curItem, quantity: 0 };
    const completedItem = itemsCompleted[sku];
    completedItem.quantity = completedItem.quantity + quantity;
  });
  return { itemsCompleted, itemsRemaining };
}

function generateCompletedOrderPhysicalItemsState(
  allPhysicalItems = [],
  assignedPhysicalItemIds = new Set(),
  incomingPackages = [],
  orderCreationType,
) {
  const assignedQuantityById = {};
  const collectAssignedQuantities = (packages = []) => {
    packages.forEach((orderPackage) => {
      (orderPackage.items || []).forEach((item) => {
        (item.physicalItems || []).forEach((physicalItem) => {
          if (physicalItem.id == null) return;
          assignedQuantityById[physicalItem.id] =
            (assignedQuantityById[physicalItem.id] || 0) +
            (physicalItem.quantity || 1);
        });
      });
      collectAssignedQuantities(orderPackage.childPackages || []);
    });
  };
  collectAssignedQuantities(incomingPackages);

  const physicalItemsObj = {};
  allPhysicalItems.forEach((pi) => {
    const { id, sku } = pi;
    if (orderCreationType !== 'Extensiv') {
      if (!hasPhysicalItemTrackingValues(pi)) return;
      const remainingQuantity = Math.max(
        0,
        (pi.quantity || 1) - (assignedQuantityById[id] || 0),
      );
      if (remainingQuantity > 0) {
        physicalItemsObj[sku] = physicalItemsObj[sku] || [];
        physicalItemsObj[sku].push({ ...pi, quantity: remainingQuantity });
      }
    } else if (!assignedPhysicalItemIds.has(id + '')) {
      physicalItemsObj[sku] = physicalItemsObj[sku] || [];
      physicalItemsObj[sku].push(pi);
    }
  });
  return physicalItemsObj;
}

export function generateReshipPayload({
  packages,
  allPhysicalItems,
  orderData,
  status = 'Closed',
  reshipState,
}) {
  const data = generateOrderPayload({
    packages,
    allPhysicalItems,
    orderData,
    status,
  });

  const {
    entityIds,
    hasOverrideCarrierInfo,
    hasOverrideShipToInfo,
    reshipReturnReason,
    overrideShouldChargeToCustomer,
    warehouseID,
    carrierRequestedName,
    carrierID,
    carrierServiceCode,
    fulfillmentType,
    shippingSelectionPreference,
    thirdPartyAccountNumber,
    insuranceCost,
    requiresDeliveryConfirmation,
    shipToCompanyName,
    shipToFirstName,
    shipToLastName,
    shipToAddress1,
    shipToAddress2,
    shipToCity,
    shipToStateID,
    shipToZip,
    shipToCountryID,
    shipToPhone,
  } = reshipState || {};

  const { shipToContactId, overrideOrderShipmentID } = entityIds || {};

  data.overrideOrderShipmentID = overrideOrderShipmentID;
  data.warehouseID = warehouseID;
  data.reshipReturnReason = reshipReturnReason;
  data.overrideShouldChargeToCustomer = overrideShouldChargeToCustomer;

  if (hasOverrideShipToInfo) {
    data.shipToContactOverride = formatContactApi({
      id: shipToContactId,
      companyName: shipToCompanyName,
      firstName: shipToFirstName,
      lastName: shipToLastName,
      address1: shipToAddress1,
      address2: shipToAddress2,
      city: shipToCity,
      stateId: shipToStateID,
      countryId: shipToCountryID,
      zip: shipToZip,
      phone: shipToPhone,
    });
  }

  if (hasOverrideCarrierInfo) {
    data.carrierOverrideData = {
      carrierID,
      carrierName: carrierRequestedName,
      carrierServiceCode,
      thirdPartyAccountNumber,
      fulfillmentType,
      shippingSelectionPreference,
      insuranceCost,
      requiresDeliveryConfirmation: requiresDeliveryConfirmation === 'yes',
    };
  }

  return data;
}

// freight

export function generateFreightOrderPayload({
  packages,
  allPhysicalItems,
  orderData,
  status = 'None',
  isFreightPalletMode = true,
}) {
  const {
    carrierInfo,
    firstValidation,
    items,
    lineItems,
    notes,
    shipToContact,
    ...otherOrderData
  } = orderData;
  const payload = { ...otherOrderData, status };
  let remainingDbPhysicalItems = allPhysicalItems;

  const packagesArr = Object.keys(packages).map((key) => {
    const currentPackage = packages[key];
    const {
      packagePayload: generatedPackage,
      remainingDbPhysicalItems: updatedRemaining,
    } = generatePackageData({
      currentPackage,
      allPhysicalItems,
      remainingDbPhysicalItems,
      isFreightPalletMode,
      orderCreationType: orderData.orderCreationType,
      orderId: orderData.id,
    });
    remainingDbPhysicalItems = updatedRemaining;

    if (currentPackage.childPackages) {
      const childPackages = Object.keys(currentPackage.childPackages).map(
        (cp) => {
          const currentChildPackage = currentPackage.childPackages[cp];
          const {
            packagePayload: childPackage,
            remainingDbPhysicalItems: childUpdatedRemaining,
          } = generatePackageData({
            currentPackage: currentChildPackage,
            allPhysicalItems,
            remainingDbPhysicalItems,
            isChildPackage: true,
            isFreightPalletMode,
            orderCreationType: orderData.orderCreationType,
            orderId: orderData.id,
          });
          remainingDbPhysicalItems = childUpdatedRemaining;
          return childPackage;
        },
      );

      generatedPackage.childPackages = childPackages;
    }

    return generatedPackage;
  });

  payload.packages = packagesArr;
  return payload;
}

function generatePackageData({
  currentPackage,
  allPhysicalItems,
  remainingDbPhysicalItems,
  isChildPackage,
  isFreightPalletMode,
  orderCreationType,
  orderId,
}) {
  const {
    items,
    metadata = {},
    material,
    ...otherPackageValues
  } = currentPackage;

  const isPickAndStick = !!metadata.isPickAndStick;
  const isPickAndShip = !!metadata.isPickAndShip;
  const isCustomMode = !!metadata.isCustomMode;

  let mode;
  if (!isFreightPalletMode || isChildPackage) {
    mode = isCustomMode
      ? 'Unknown'
      : isPickAndStick
        ? 'PickAndStick'
        : 'PickAndPack';
  } else {
    mode = isPickAndShip ? 'PickAndShip' : 'PrepPallet';
  }

  let payload = {
    ...otherPackageValues,
    mode,
    isSealed: metadata.isCompleted,
    weightPounds: metadata.weightPounds,
    isOnPallet: isChildPackage && isFreightPalletMode,
    items:
      items &&
      Object.keys(items).map((k) => {
        const item = items[k];
        if (!allPhysicalItems) {
          return item;
        }
        const { physicalItems, ...rest } = item;
        const { itemPhysicalItems, remainingDbItems } =
          generateItemPhysicalItems({
            sku: k,
            itemPhysicalItems: physicalItems,
            dbPhysicalItems: remainingDbPhysicalItems,
            itemQty: item.quantity,
            orderCreationType,
            orderId,
          });
        if (remainingDbItems) {
          remainingDbPhysicalItems = remainingDbItems;
        }
        return {
          ...rest,
          physicalItems: itemPhysicalItems,
        };
      }),
    materials:
      material &&
      Object.keys(material).map((k) => {
        const {
          sku,
          dimensions,
          description,
          packingMaterialTypeDisplay,
          ...otherMaterial
        } = material[k];
        return {
          ...otherMaterial,
          packingMaterialID: sku,
        };
      }),
  };
  // package dimensions
  if (isPickAndStick || isCustomMode) {
    payload = { ...payload, ...getPickAndStickDimensions(currentPackage) };
  }
  return { packagePayload: payload, remainingDbPhysicalItems };
}

/**
 * Updates the items remaining, items completed, and packages state
 * @param {object} data The incoming payload
 * @param {object} itemsObj The order items as object with the sku as the key
 * @param {function} dispatch
 */
export function syncIncomingCompletedFreightOrderFields(data, itemsObj) {
  const {
    packages: incomingPackages,
    status,
    physicalItems: allPhysicalItems,
  } = data;
  const assignedPhysicalItemIds = new Set();

  const packages = incomingPackages.reduce((acc, cur, index) => {
    const {
      mode,
      weightPounds,
      materials,
      items: incomingItems,
      childPackages,
      isSealed,
      ...rest
    } = cur;

    const parentMaterials = generateIncomingMaterial(materials);

    const generatedParentItems = generateIncomingItems({
      items: incomingItems,
      itemsObj,
    });

    if (generatedParentItems) {
      Object.keys(generatedParentItems).forEach((i) => {
        Object.keys(generatedParentItems[i]?.physicalItems ?? {}).forEach(
          (pid) => assignedPhysicalItemIds.add(pid),
        );
      });
    }

    const hasChildPackages =
      Array.isArray(childPackages) && childPackages.length;

    const generatedChildPackages = {};
    if (hasChildPackages) {
      childPackages.forEach((cp, i) => {
        const {
          mode: childMode,
          weightPounds: childWeight,
          materials: childMaterials,
          items: childItems,
          isSealed: childIsSealed,
          childPackages: _childPackages,
          ...otherChild
        } = cp;

        const generatedChildMaterials =
          generateIncomingMaterial(childMaterials);
        const generatedChildItems = generateIncomingItems({
          items: childItems,
          itemsObj,
        });

        Object.keys(generatedChildItems).forEach((i) => {
          Object.keys(generatedChildItems[i]?.physicalItems ?? {}).forEach(
            (pid) => assignedPhysicalItemIds.add(pid),
          );
        });

        generatedChildPackages[i + 1] = {
          ...otherChild,
          weightPounds: childWeight,
          metadata: {
            isCompleted: childIsSealed,
            isPickAndStick: childMode === 'PickAndStick',
            weightPounds: childWeight,
          },
          material: generatedChildMaterials,
          items: generatedChildItems,
        };
      });
    }

    acc[index + 1] = {
      ...rest,
      weightPounds,
      metadata: {
        isCompleted: isSealed,
        isPickAndShip: mode === 'PickAndShip',
        lotNumber: incomingItems[0]?.physicalItems?.[0]?.lotNumber,
        weightPounds,
      },
      material: parentMaterials,
      items: generatedParentItems,
      // A PrepPallet remains a container while empty. Its mode, rather than
      // its current contents, determines the shape used by rendering/editing.
      childPackages:
        mode === 'PrepPallet' || hasChildPackages
          ? generatedChildPackages
          : undefined,
    };
    return acc;
  }, {});

  const physicalItems = generateCompletedOrderPhysicalItemsState(
    allPhysicalItems,
    assignedPhysicalItemIds,
    incomingPackages,
    data.orderCreationType,
  );

  if (status === orderStatuses.HOLD || status === orderStatuses.PAUSED) {
    const { itemsCompleted, itemsRemaining } = syncItemsRemainingCompleted(
      incomingPackages,
      itemsObj,
    );
    return { packages, physicalItems, itemsCompleted, itemsRemaining };
  }
  return { packages, physicalItems, itemsCompleted: itemsObj };
}

function generateIncomingMaterial(materials) {
  return materials.reduce((accM, curM) => {
    const {
      packingMaterialID,
      packingMaterialTypeDisplay,
      lengthInches,
      widthInches,
      heightInches,
    } = curM;
    let key = packingMaterialID;
    if (packingMaterialTypeDisplay === packingMaterialTypes.BOX) {
      key = 'box';
    }
    accM[key] = {
      ...curM,
      sku: packingMaterialID,
      description: packingMaterialTypeDisplay,
      dimensions: getDimensionsString({
        height: heightInches,
        length: lengthInches,
        width: widthInches,
      }),
    };
    return accM;
  }, {});
}

// API properties are camelCase; packing state uses the canonical UOM names.
// Keep only unit counts so metadata such as customerItemID cannot become a UOM.
function normalizePackedUnitCounts(counts) {
  if (!counts) return counts;
  return Object.fromEntries(
    Object.values(unitOfMeasure).flatMap((unit) => {
      const count = counts[unit] ?? counts[unit.toLowerCase()];
      return count == null ? [] : [[unit, count]];
    }),
  );
}

function generateIncomingItems({ items, itemsObj }) {
  if (!items) return {};

  return items.reduce((accI, curI) => {
    const { sku, quantity, id, physicalItems, packedPerUnitOfMeasureCount } =
      curI;
    const item = itemsObj[sku] || {};
    let physicalItemsObj;
    if (Array.isArray(physicalItems)) {
      physicalItemsObj = physicalItems.reduce((acc, physicalItem) => {
        // Package-only PI, such as Enable-on-Outbound serials, have no
        // order-physical-item ID. Give them a UI state key without fabricating
        // an ID that the backend will interpret as an order PI reference.
        acc[physicalItem.id ?? makeuuid(6)] = physicalItem;
        return acc;
      }, {});
    }
    accI[sku] = {
      ...item,
      quantity,
      id,
      physicalItems: physicalItemsObj,
      packedPerUnitOfMeasureCount: normalizePackedUnitCounts(
        packedPerUnitOfMeasureCount,
      ),
    };
    return accI;
  }, {});
}
